import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, ExternalLink, Bookmark, BookmarkCheck, Trash2, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ArticleCard component - moved outside to prevent re-creation on every render
const ArticleCard = ({ article, showSaveButton = true, showDeleteButton = false, onSave, onDelete }) => (
  <Card className="bg-card border-border/50 p-6 hover:border-primary/50 transition-colors" data-testid={`article-${article.id || article.url}`}>
    <div className="flex flex-col">
      {/* Content */}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-xs">{article.publisher}</Badge>
          <span className="text-xs text-muted-foreground">
            {new Date(article.published_date || article.created_at).toLocaleDateString()}
          </span>
        </div>
        
        <h3 className="font-manrope font-bold text-lg text-foreground mb-2">{article.title}</h3>
        
        {article.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{article.description}</p>
        )}
        
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            size="sm"
            data-testid="read-article-button"
          >
            <a href={article.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Read Article
            </a>
          </Button>
          
          {showSaveButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSave(article)}
              data-testid="save-article-button"
              className="flex items-center gap-2"
            >
              <Bookmark className="w-4 h-4" />
              Save
            </Button>
          )}
        </div>
      </div>

      {/* Delete button at bottom - full width to avoid Emergent logo clash */}
      {showDeleteButton && (
        <div className="mt-4 pt-4 border-t border-border/30">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(article.id)}
            data-testid="delete-article-button"
            className="w-full h-9 text-xs touch-none"
          >
            <Trash2 className="w-3 h-3 mr-2" />
            Delete Article
          </Button>
        </div>
      )}
    </div>
  </Card>
);

const News = ({ user, selectedArtist }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [savedArticles, setSavedArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error.message);
    }
  }, []);

  const fetchSavedArticles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/news/saved${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch saved articles:', error.message);
    }
  }, [selectedArtist]);

  const fetchTrendingNews = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news/trending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrendingArticles(response.data.articles);
    } catch (error) {
      console.error('Failed to fetch trending news:', error.message);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
    fetchSavedArticles();
    fetchTrendingNews();
  }, [user?.role, fetchArtists, fetchSavedArticles, fetchTrendingNews]);

  const searchArtistNews = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter an artist name');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news/search/${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticles(response.data.articles);
      toast.success(`Found ${response.data.total} articles`);
    } catch (error) { // Silent fail
      toast.error('Failed to search news');
    } finally {
      setLoading(false);
    }
  };

  const saveArticle = async (article) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/news/save`, article, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Article saved');
      fetchSavedArticles();
    } catch (error) { // Silent fail
      toast.error('Failed to save article');
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Remove this article from saved items?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/news/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Article removed');
      fetchSavedArticles();
    } catch (error) { // Silent fail
      toast.error('Failed to remove article');
    }
  };

  return (
    <div className="space-y-8" data-testid="news-page">
      <div>
        <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">News</h1>
        <p className="text-muted-foreground text-base mt-2">Track artist mentions and music industry news</p>
      </div>

      {/* Search Box */}
      <Card className="bg-card border-border/50 p-6">
        <div className="flex gap-2">
          <Input
            placeholder="Search for artist news..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchArtistNews()}
            data-testid="news-search-input"
            className="flex-1 bg-input/50 border-border rounded-xl"
          />
          <Button
            onClick={searchArtistNews}
            disabled={loading}
            data-testid="search-news-button"
            className="bg-primary hover:bg-primary/90"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="search" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="w-4 h-4 mr-2" />
            Search Results
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved">
            <BookmarkCheck className="w-4 h-4 mr-2" />
            Saved ({savedArticles.length})
          </TabsTrigger>
          <TabsTrigger value="trending" data-testid="tab-trending">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trending
          </TabsTrigger>
        </TabsList>

        {/* Search Results Tab */}
        <TabsContent value="search" className="space-y-4 mt-6">
          {articles.length === 0 ? (
            <Card className="bg-card border-border/50 p-12 text-center">
              <p className="text-muted-foreground">Search for an artist to find news mentions</p>
            </Card>
          ) : (
            articles.map((article) => (
              <ArticleCard key={article.url || article.title} article={article} showSaveButton={true} onSave={saveArticle} onDelete={deleteArticle} />
            ))
          )}
        </TabsContent>

        {/* Saved Articles Tab */}
        <TabsContent value="saved" className="space-y-4 mt-6">
          {savedArticles.length === 0 ? (
            <Card className="bg-card border-border/50 p-12 text-center">
              <p className="text-muted-foreground">No saved articles yet</p>
            </Card>
          ) : (
            savedArticles.map((article) => (
              <ArticleCard key={article.id} article={article} showSaveButton={false} showDeleteButton={true} onSave={saveArticle} onDelete={deleteArticle} />
            ))
          )}
        </TabsContent>

        {/* Trending Tab */}
        <TabsContent value="trending" className="space-y-4 mt-6">
          {trendingArticles.length === 0 ? (
            <Card className="bg-card border-border/50 p-12 text-center">
              <p className="text-muted-foreground">Loading trending news...</p>
            </Card>
          ) : (
            trendingArticles.map((article) => (
              <ArticleCard key={article.url || article.title} article={article} showSaveButton={true} onSave={saveArticle} onDelete={deleteArticle} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default News;